import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, inject, OnInit, Optional, ViewChild } from '@angular/core';
import {
    IAttributeConfData,
    IAttributeConfDataOption,
    ProductConfigurableOptionsComponentDef
} from '@df/catalog-ui/product/configurable-options/product-configurable-options.component.def';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { IProductConfigurableSelection, IProductDataV3, Product } from '@df/catalog/product/product';
import { DfEventService } from '@df/core/service/df-event';
import { Status } from '@df/core/status';
import { isInViewport } from '@df/dom/fn/is-in-viewport';
import { ScrollToService } from '@df/dom/scroll/scroll-to.service';
import { NgInject } from '@df/ng/ng-inject';
import { TimeoutService } from '@df/ng/timeout.service';
import { OptionLabelPipe } from '@df/ui/common/option-label.pipe';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { DfModalService } from '@df/ui/modal/df-modal.service';
import { TranslationModule } from '@df/ui/translation/translation.module';
import difference from 'lodash-es/difference';
import { SizebayComponent } from 'projects/app-default/src/app/module/sizebay/sizebay.component';
import { BoxComponent } from 'projects/app-default/src/app/ui/display/box/box.component';
import { NProductSizeGuideModal } from '../../../../cms/modal/product-size-guide/product-size-guide-modal.interface';
import { SwatchComponent } from '../../../attribute/swatch/swatch.component';
import { LocalProduct } from '../../../model/product/product.decorator';
import { ProductViewComponent } from '../product-view.component';
import {
    ConfigurableOptionWarningService,
    TConfigurableOptionWarning
} from './configurable-option-warning/configurable-option-warning.service';
import { ConfigurableOptionWarningTriggerDirective } from './configurable-option-warning/configurable-option-warning.trigger.directive';
import { ConfigurableOptionsErrorService, IConfigurableError } from './configurable-options-error/configurable-options-error.service';
import {
    ADD_TO_BASKET_WITHOUT_OPTION_EVENT,
    ProductConfigurableOptionDropdownComponent
} from './dropdown/product-configurable-option-dropdown.component';
export const SELECTION_CHANGE_EVENT = 'configurable.selection.change';
declare module '@df/catalog-ui/product/configurable-options/product-configurable-options.component.def' {
    interface IAttributeConfDataOption extends Catalog.IAttributeOption {
        isHovered?: boolean;
    }
    interface IAttributeConfData {
        hidden?: boolean;
    }
}

export const MULTIPLE_SELECTOR_GROUP = ['size', 'boot_width', 'boot_height'];

export const ATTR_WITH_DROPDOWN = ['size'];

@Component({
    selector: 'product-configurable-options',
    templateUrl: 'product-configurable-options.html',
    standalone: true,
    imports: [
        BoxComponent,
        NgClass,
        OptionLabelPipe,
        ProductConfigurableOptionDropdownComponent,
        ConfigurableOptionWarningTriggerDirective,
        SizeClassDirective,
        SwatchComponent,
        TranslationModule,
        SizebayComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductConfigurableOptionsComponent extends ProductConfigurableOptionsComponentDef implements OnInit {
    /**
     * Cms modal service
     */
    @NgInject(DfModalService)
    private dfModalService!: DfModalService;
    @NgInject(ConfigurableOptionWarningService)
    private configurableOptionWarningService!: ConfigurableOptionWarningService;
    private initialProduct!: Product;
    private initialStorageData: IProductConfigurableSelection | undefined;
    protected configurableError?: IConfigurableError;
    private dfEventService = inject(DfEventService);
    private scrollToService = inject(ScrollToService);
    /**
     * Size gide modal open
     */
    readonly sizeGuideStatus = new Status();
    @ViewChild('sizebayRef')
    private sizebayComponent!: SizebayComponent;
    constructor(
        ref: ChangeDetectorRef,
        timeoutService: TimeoutService,
        @Optional()
        private productViewComponent: ProductViewComponent,
        private configurableOptionsErrorService: ConfigurableOptionsErrorService,
        private elementRef: ElementRef
    ) {
        super(ref, timeoutService);
        // selection may be change outside of component, we need to listen to changes
        this.subscriptions.add(
            this.app.events.on(SELECTION_CHANGE_EVENT).subscribe(() => {
                this.timeoutService.setTimeout(() => {
                    this.sizebayComponent?.load();
                }, 100);
                this.onDataChange();
            })
        );
        this.subscriptions.add(
            this.configurableOptionsErrorService.error.subscribe(errorData => {
                this.configurableError = errorData;
                this.detectChanges();
            })
        );
        this.dfEventService.before(event => {
            if (event.id === ADD_TO_BASKET_WITHOUT_OPTION_EVENT) {
                // scroll to top on desktop (cant be inViewPort cause of sticky panel to gallery)
                if (this.app.client.sizeId > 1 && !isInViewport(this.elementRef.nativeElement, 0, 'y-whole')) {
                    this.scrollToService.scrollTo(100);
                }
            }
        });
    }
    override ngOnInit() {
        if (this.productViewComponent) {
            this.initialProduct = this.productViewComponent.model;
            this.initialStorageData = this.dataStorage;
        }
        super.ngOnInit();
    }
    /**
     * If has size guide
     */
    hasSizeGuide(attribute: IAttributeConfData): boolean {
        return (
            attribute.code === 'size' &&
            (!!(this.parent as LocalProduct).sizeGuideBlock ||
                !!(this.product as LocalProduct).sizeGuideBlock ||
                (this.app.client.isCms && !!(this.product as LocalProduct).sizeGuideVariant))
        );
    }
    /**
     * Opens size guide
     */
    openSizeGuide() {
        this.dfModalService.open(this.sizeGuideStatus, NProductSizeGuideModal.BLOCK_ID, (this.product as LocalProduct).sizeGuideVariant);
    }
    hasWarning(option: IAttributeConfDataOption): boolean {
        return this.configurableOptionWarningService.getProductWarning(option)?.id === TConfigurableOptionWarning.discontinued;
    }
    isDisabled(option: IAttributeConfDataOption): boolean {
        const product = <LocalProduct<IProductDataV3>>option.product;
        return (
            (!product?.isSalable() && !this.hasWarning(option)) ||
            this.configurableOptionWarningService.getProductWarning(option)?.id === TConfigurableOptionWarning.unavailable ||
            product.isComingSoon
        );
    }
    /**
     * Checks if option can be picked
     */
    override canUseOption(attributeCode: string, optionValue: number): boolean {
        const mapItem = this.attributes.find(i => i.code === attributeCode);
        if (mapItem) {
            const option = mapItem.options.find(i => i.value === optionValue);
            return !!option?.product;
        }
        return false;
    }
    /**
     * Make sure attrs are ordered
     */
    protected override setAttributes(attributes: IAttributeConfData[] | undefined) {
        if (!attributes) {
            attributes = [];
        }
        const orderedAttrs = ['color', 'size'].map(i => attributes?.find(a => a.code === i)).filter(i => !!i);
        attributes = [...orderedAttrs, ...difference(attributes, orderedAttrs)].filter(i => !!i?.model?.data);
        super.setAttributes(
            this.preselectSingles
                ? attributes.map(attribute =>
                      attribute.code === 'size'
                          ? {
                                ...attribute,
                                hidden:
                                    (attribute.options.length === 1 && attribute.options[0].label === 'One Size') ||
                                    !attribute.options.length
                            }
                          : { ...attribute, hidden: attribute.options.length <= 1 }
                  )
                : attributes
        );
    }
    protected override getOptionsMap(code: string): IAttributeConfDataOption[] {
        return super.getOptionsMap(code).filter(i => i.product);
    }
    protected override emitChange() {
        // first sync all components options
        this.app.events.broadcast(SELECTION_CHANGE_EVENT);
        // then emit change to layout component
        super.emitChange();
    }
    protected override checkStockForOptionPrint(product: LocalProduct, code: string, option: Catalog.IAttributeOption): boolean {
        if (!product.parent?.isConfigurable) {
            return false;
        }
        if (
            this.initialProduct === product || // matching product we entered (simple product direct entry)
            this.initialStorageData?.[code] === option.value || // matches initial selection (configurable product direct entry)
            !product.isSoldOut || // product is not discontinued
            !product.isOutOfStock || // product is not out of stock
            this.initialProduct?.directChildren.includes(product) // product is child of initial product (configurable product direct entry
        ) {
            return true;
        }
        return false;
    }
    protected get isErrorVisible(): boolean {
        return !!this.configurableError?.attribute && this.configurableError?.shouldShow;
    }

    protected sortedAttributesOptions(attrCode: string, attrOptions: IAttributeConfDataOption[]) {
        const attrOptionsOrder = (this.product as LocalProduct).attrsOptionsOrder
            ?.find(e => e.attr === attrCode)
            ?.order?.map(i => i.optionLabel?.toLocaleLowerCase());
        if (attrOptionsOrder) {
            return attrOptions.sort((a, b) => {
                const optionIndex1 = attrOptionsOrder.indexOf(
                    typeof a.label === 'string' ? a.label?.toLocaleLowerCase() : a.label.toString()
                );
                const optionIndex2 = attrOptionsOrder.indexOf(
                    typeof b.label === 'string' ? b.label?.toLocaleLowerCase() : b.label.toString()
                );
                return (optionIndex1 > -1 ? optionIndex1 : Infinity) - (optionIndex2 > -1 ? optionIndex2 : Infinity);
            });
        }
        return attrOptions;
    }

    protected displayDropdown(attributeCode: string): boolean {
        return ATTR_WITH_DROPDOWN.includes(attributeCode) || this.isAttributeMultiple(attributeCode);
    }

    protected isAttributeMultiple(attributeCode: string): boolean {
        return this.hasMultiple && MULTIPLE_SELECTOR_GROUP.includes(attributeCode);
    }

    get hasMultiple(): boolean {
        return (
            (this.product as LocalProduct).hasMultipleConfigurableSelectors ||
            (this.product.parent as LocalProduct)?.hasMultipleConfigurableSelectors
        );
    }

    get displayDropdownWarnings(): boolean {
        return !this.hasMultiple || !this.attributes.filter(attribute => !this.data[attribute.code]).length;
    }
}
